"use client";

import { useRef } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { cn } from "@/shared/lib/utils";
import type { HomeCtaSectionContent } from "@/features/home/types/home-page-content";

const images = [
  {
    id: 1,
    src: "/homepage/clearity.webp",
    className: "w-28 h-36 sm:w-40 sm:h-52 md:w-48 md:h-64",
    initial: { rotate: -15, x: 0, y: 0, opacity: 0, scale: 0.8 },
    animate: { rotate: -12, x: "-210%", y: "30%", opacity: 1, scale: 0.9 },
    zIndex: 10,
  },
  {
    id: 2,
    src: "/homepage/heard.webp",
    className: "w-32 h-40 sm:w-44 sm:h-56 md:w-52 md:h-72",
    initial: { rotate: -8, x: 0, y: 0, opacity: 0, scale: 0.8 },
    animate: { rotate: -6, x: "-110%", y: "10%", opacity: 1, scale: 0.95 },
    zIndex: 20,
  },
  {
    id: 3,
    src: "/images/home/emotional-connection-bg.png",
    className: "w-36 h-44 sm:w-48 sm:h-64 md:w-56 md:h-[20rem]",
    initial: { rotate: 0, x: 0, y: 0, opacity: 0, scale: 0.8 },
    animate: { rotate: 0, x: "0%", y: "-5%", opacity: 1, scale: 1 },
    zIndex: 30, // center front
  },
  {
    id: 4,
    src: "/homepage/hero-image.webp",
    className: "w-32 h-40 sm:w-44 sm:h-56 md:w-52 md:h-72",
    initial: { rotate: 8, x: 0, y: 0, opacity: 0, scale: 0.8 },
    animate: { rotate: 6, x: "110%", y: "10%", opacity: 1, scale: 0.95 },
    zIndex: 20,
  },
  {
    id: 5,
    src: "/homepage/compectoclear.webp",
    className: "w-28 h-36 sm:w-40 sm:h-52 md:w-48 md:h-64",
    initial: { rotate: 15, x: 0, y: 0, opacity: 0, scale: 0.8 },
    animate: { rotate: 12, x: "210%", y: "30%", opacity: 1, scale: 0.9 },
    zIndex: 10,
  },
];

interface HomeCtaSectionProps {
  content: HomeCtaSectionContent;
}

export function HomeCtaSection({ content }: HomeCtaSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // We could use scroll to animate them outwards, but a spring animation
  // on view provides a very consistent and premium feel.

  return (
    <section
      ref={containerRef}
      className="relative flex w-full flex-col items-center justify-center overflow-hidden border-t border-slate-100 bg-white py-24 font-sans sm:py-32"
    >
      <div className="relative flex w-full max-w-7xl flex-col items-center px-4 sm:px-6 lg:px-8">
        {/* Images Fan Container */}
        <div className="pointer-events-none relative mt-8 mb-16 flex h-64 w-full max-w-3xl items-start justify-center sm:mb-24 sm:h-80 md:h-96 lg:mb-32">
          {images.map((img, i) => (
            <motion.div
              key={img.id}
              className={cn("absolute top-0 origin-bottom", img.className)}
              style={{ zIndex: img.zIndex }}
              initial={img.initial}
              whileInView={img.animate}
              viewport={{ once: true, margin: "-100px" }}
              transition={{
                type: "spring",
                stiffness: 70,
                damping: 18,
                delay: 0.1 + i * 0.05,
              }}
            >
              <div className="relative h-full w-full overflow-hidden rounded-2xl border-4 border-white/80 bg-slate-100 shadow-2xl shadow-black/10 sm:rounded-3xl">
                <Image
                  src={img.src}
                  alt={content.imageAlts[i] ?? ""}
                  fill
                  className="object-cover"
                  sizes="(max-width: 640px) 150px, (max-width: 768px) 200px, 300px"
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Text Area */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative z-40 mt-10 max-w-4xl px-4 text-center sm:mt-0"
        >
          <h2 className="text-4xl leading-[1.1] font-semibold tracking-tight text-balance text-foreground sm:text-6xl md:text-[5rem] md:leading-[1.05] lg:text-[5.5rem]">
            {content.title}
          </h2>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 100, delay: 0.6 }}
          >
            <Link
              href={content.buttonHref}
              className="mt-10 inline-flex items-center justify-center rounded-full bg-primary px-10 py-4 text-[15px] font-medium text-primary-foreground shadow-xl shadow-primary/20 transition-all hover:scale-105 hover:bg-primary/90 active:scale-95 sm:mt-12 sm:px-12 sm:py-5 sm:text-base"
            >
              {content.buttonLabel}
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
